/// <amd-module name="Agile/Directory/Components/DirectoryContentView" />
import { AgileAssetPaths } from "Agile/Assets/AgileAssets";
import { AssetResolver } from "Agile/Common/Components/AssetResolver/AssetResolver";
import { HubError } from "Agile/Common/Components/HubError/HubError";
import { ExceptionInfo } from "Agile/Contracts/Common";
import { ConnectedDirectoryFilterBar } from "Agile/Directory/Components/DirectoryFilterBar/DirectoryFilterBar";
import { DEFAULT_NAME_COLUMN, DEFAULT_TEAM_COLUMN, DirectoryGrid, IDirectoryRow, IGridRow } from "Agile/Directory/Components/DirectoryGrid";
import { IDirectoryArtifact, IDirectoryViewState, IFavoriteData } from "Agile/Directory/DirectoryContracts";
import { DirectoryActions } from "Agile/Directory/Redux/DirectoryActions";
import {
    createInstanceSelectors,
    getExceptionInfo,
    getFavoriteMap,
    isAllSectionLoading,
    isFilterVisible
} from "Agile/Directory/Redux/DirectorySelectors";
import { NavigationActions, isNavigating } from "VSS/Redux/Modules/Navigation";
import { FavoriteActions } from "Agile/Directory/Redux/Favorites/FavoriteActions";
import * as DirectoryResources from "Agile/Directory/Resources";
import { DirectoryGroupKeys } from "DirectoryConstants";
import { IColumn } from "OfficeFabric/DetailsList";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import * as React from "react";
import { connect } from "react-redux";
import { IProjectData } from "Tfs/Platform/Page";
import { currentProject } from "Tfs/ReduxCommon/Modules/TfsPageData/TfsPageData";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { isFilterActive } from "VSS/Redux/Deprecated/HubViewState";
import { TTIMonitorActions } from "VSS/Redux/Modules/TTIMonitor";
import { ZeroData } from "VSSUI/Components/ZeroData";
import { Header, TitleSize } from "VSSUI/Header";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Page } from "VSSUI/Page";
import { IFilter } from "VSSUI/Utilities/Filter";
import { IHubViewState } from "VSSUI/Utilities/HubViewState";
import { IVssIconProps, VssIcon } from "VSSUI/VssIcon";

/** Props provided to the connected component */
export interface IDirectoryContentViewOwnProps<T extends IDirectoryArtifact> {
    /** The hub view state to use in this area */
    hubViewState: IHubViewState;

    ttiScenarioProps: { areaName: string; featureName: string };

    /** Command bar items to display in the header */
    commandBarItems?: IHeaderCommandBarItem[];

    /** View actions to display in the header */
    viewItems?: IHeaderCommandBarItem[];

    /** Any custom columns to render */
    customColumns?: IColumn[];

    /** Custom render function to render cells */
    onRenderCell?: (
        item: IDirectoryRow<T>,
        index: number,
        column: IColumn,
        defaultRenderer?: (item: IDirectoryRow<T>, index: number, column: IColumn) => JSX.Element
    ) => JSX.Element;

    /** Render a custom filter bar */
    onRenderFilterBar?: (filter: IFilter) => JSX.Element;

    /** Render area for user message bars */
    onRenderMessageArea?: () => JSX.Element | undefined;

    artifactIcon?: IVssIconProps;

    /** Name of the Directory Artifact */
    artifactName?: string;
}

/** Props provided from the redux store */
export interface IDirectoryContentViewMappedProps<T extends IDirectoryArtifact> {
    /** The current project context */
    currentProject: IProjectData;

    /** The favorites that have been loaded */
    favorites: { [key: string]: IFavoriteData };

    /** The directory rows to display */
    directoryRows: IGridRow<T>[];

    /** Are the rows in grouped mode? */
    isFilterActive: boolean;

    /** Is the filter bar visible */
    isFilterVisible: boolean;

    /** Is the all section loading? */
    isAllSectionLoading: boolean;

    isNavigating: boolean;

    /** Any present exception info */
    exceptionInfo?: ExceptionInfo;
}

export interface IDirectoryContentViewProps<T extends IDirectoryArtifact>
    extends IDirectoryContentViewMappedProps<T>,
        IDirectoryContentViewOwnProps<T> {}

/**
 * Component which displays the Directory Grid
 */
export class DirectoryContentView<T extends IDirectoryArtifact> extends React.Component<IDirectoryContentViewProps<T> & typeof Actions> {
    public render(): JSX.Element {
        const { artifactName, onRenderMessageArea, isFilterActive, isFilterVisible, isNavigating, commandBarItems, viewItems } = this.props;

        if (isNavigating) {
            return (
                <div className="absolute-fill flex-column flex-center justify-center">
                    <Spinner size={SpinnerSize.large} label={DirectoryResources.LoadingMessage} />
                </div>
            );
        }

        return (
            <Page className="directory-page v-scroll-auto absolute-fill">
                {onRenderMessageArea && onRenderMessageArea()}
                <Header
                    title={artifactName}
                    titleSize={TitleSize.Large}
                    commandBarItems={[
                        {
                            id: "filter",
                            iconProps: {
                                iconName: isFilterActive ? "FilterSolid" : "Filter"
                            },
                            ariaLabel: DirectoryResources.FilterAriaLabel,
                            onActivate: this._onFilterToggled,
                            subtle: true
                        },
                        ...(viewItems || []),
                        ...(commandBarItems || [])
                    ]}
                />
                {isFilterVisible && this._renderFilterBar()}
                {this._renderContent()}
            </Page>
        );
    }

    public componentDidMount(): void {
        this.props.markTti(this.props.ttiScenarioProps.areaName, this.props.ttiScenarioProps.featureName);
    }

    private _renderContent(): JSX.Element {
        const { exceptionInfo, isAllSectionLoading, isFilterActive, directoryRows } = this.props;

        if (exceptionInfo) {
            return this._renderExceptionInfo();
        } else if (isFilterActive && isAllSectionLoading) {
            return this._renderFullLoading();
        } else if (isFilterActive && directoryRows.length === 0) {
            return this._renderEmptyFilter();
        } else {
            return this._renderDirectoryGrid();
        }
    }

    private _renderExceptionInfo(): JSX.Element {
        return <HubError className="page-content-top" exceptionsInfo={[this.props.exceptionInfo!]} />;
    }

    private _renderFullLoading(): JSX.Element {
        return <Spinner className="page-content-top" label={DirectoryResources.LoadingMessage} size={SpinnerSize.large} />;
    }

    private _renderEmptyFilter(): JSX.Element {
        return (
            <AssetResolver assets={[AgileAssetPaths.NoResultsFound]}>
                {([resolvedImagePath]) => (
                    <ZeroData
                        className="page-content-top"
                        imagePath={resolvedImagePath}
                        imageAltText=""
                        primaryText={DirectoryResources.NoResultsFound}
                    />
                )}
            </AssetResolver>
        );
    }

    private _renderDirectoryGrid(): JSX.Element {
        const { currentProject, customColumns, directoryRows, isFilterActive, onRenderCell, toggleGroup } = this.props;

        let columns;
        if (customColumns) {
            columns = [...customColumns];
        } else {
            columns = [DEFAULT_NAME_COLUMN, DEFAULT_TEAM_COLUMN];
        }

        return (
            <div className="directory-content page-content page-content-top v-scroll-auto">
                <DirectoryGrid
                    project={currentProject}
                    rows={directoryRows}
                    columns={columns}
                    isGrouped={!isFilterActive}
                    onFavoriteToggled={this._onFavoriteToggled}
                    onGroupToggled={toggleGroup}
                    onItemClicked={this._onItemClicked}
                    onRenderCell={onRenderCell}
                    onRenderZeroDataRow={this._onRenderZeroDataRow}
                />
            </div>
        );
    }

    private _renderFilterBar = (): JSX.Element => {
        const {
            hubViewState: { filter },
            onRenderFilterBar
        } = this.props;

        if (onRenderFilterBar) {
            return onRenderFilterBar(filter);
        }

        return <ConnectedDirectoryFilterBar filter={filter} />;
    };

    private _onFavoriteToggled = (item: IDirectoryRow<T>): void => {
        const { favorites, toggleFavorite } = this.props;

        const artifactId = item.data.id;
        const favoriteId = favorites[artifactId] ? favorites[artifactId].id : undefined;

        toggleFavorite(artifactId, item.data.name, item.data.favoriteType, favoriteId);
    };

    private _onFilterToggled = (): void => {
        const { toggleFilter } = this.props;

        toggleFilter();
    };

    private _onRenderZeroDataRow = (groupKey: string): JSX.Element | undefined => {
        if (groupKey === DirectoryGroupKeys.FavoriteGroupKey) {
            return (
                <FormatComponent format={DirectoryResources.FavoritesEmptyText}>
                    <VssIcon iconName="FavoriteStarFill" className="vss-FavoriteItem--starIcon" />
                    {this.props.artifactName ? this.props.artifactName.toLowerCase() : DirectoryResources.DefaultArtifactName}
                </FormatComponent>
            );
        }
    };

    private _onItemClicked = (item: IDirectoryRow<T>): void => {
        const url = item.data.url;
        this.props.navigateToUrl(url);
    };
}

export function mapStateToProps<T extends IDirectoryArtifact>(): (
    state: IDirectoryViewState<T>,
    ownProps: IDirectoryContentViewOwnProps<T>
) => IDirectoryContentViewMappedProps<T> {
    const instanceSelectors = createInstanceSelectors();
    return (state: IDirectoryViewState<T>, ownProps: IDirectoryContentViewOwnProps<T>): IDirectoryContentViewMappedProps<T> => {
        return {
            currentProject: currentProject(state)!,
            directoryRows: instanceSelectors.getDirectoryRows(state, ownProps),
            favorites: getFavoriteMap(state),
            isFilterActive: isFilterActive(state),
            isFilterVisible: isFilterVisible(state),
            isAllSectionLoading: isAllSectionLoading(state),
            isNavigating: isNavigating(state),
            exceptionInfo: getExceptionInfo(state)
        };
    };
}

export const Actions = {
    markTti: TTIMonitorActions.markTTI,
    navigateToUrl: NavigationActions.fpsNavigateToUrl,
    toggleFavorite: FavoriteActions.toggleFavorite,
    toggleGroup: DirectoryActions.rowGroupToggled,
    toggleFilter: DirectoryActions.toggleFilter
};

export const ConnectedDirectoryContentView = connect(
    mapStateToProps,
    Actions
)(DirectoryContentView);
